import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { STALE_TIME } from '@/shared/constants/app/time.constant.ts';

import { pumsemManageApi } from './pumsem-manage.api.ts';
import { pumsemKeys } from './pumsem.keys.ts';
import type {
  PumsemDivisionInput,
  PumsemDivisionPatch,
  PumsemEditionInput,
  PumsemEditionPatch,
  PumsemGroupInput,
  PumsemGroupOrderInput,
  PumsemGroupPatch,
  PumsemImportPayload,
  PumsemItemInput,
} from '../model/pumsemManage.type';

/**
 * 숨긴 판까지. 공개 목록과 **키가 다르다** — 내용이 다른 응답이다.
 *
 * `enabled` 를 열어두는 쪽(뷰어의 관리자 오버레이)에서도 같은 키를 쓴다.
 * 관리 화면과 뷰어가 같은 응답을 두 번 받지 않게 하려는 것이다.
 */
export function usePumsemManageEditions(enabled = true) {
  return useQuery({
    queryKey: [...pumsemKeys.editions(), 'manage'] as const,
    queryFn: () => pumsemManageApi.getEditions(),
    enabled,
    staleTime: STALE_TIME.SHORT,
    // 관리자가 아니면 403 이다. 재시도해도 결과가 안 바뀐다.
    retry: false,
  });
}

/**
 * 관리 트리.
 *
 * 키가 `usePumsemTree(edition, true)` 와 같다 — 같은 응답을 두 키로 캐시하면
 * 관리자가 뷰어와 관리 화면을 오갈 때 똑같은 트리를 두 번 받는다. 무효화는
 * `pumsemKeys.all` 이 둘 다 덮으므로 정합성 문제는 없었지만 왕복이 두 배였다.
 */
export function usePumsemManageTree(edition: string | null) {
  return useQuery({
    queryKey: [...pumsemKeys.all, 'tree', edition, true] as const,
    queryFn: () => pumsemManageApi.getTree(edition ?? undefined),
    staleTime: STALE_TIME.SHORT,
    retry: false,
  });
}

/** 전역 부문 목록. 판과 무관하므로 키에 edition 이 없다. */
export function usePumsemDivisions() {
  return useQuery({
    queryKey: [...pumsemKeys.all, 'divisions', 'manage'] as const,
    queryFn: () => pumsemManageApi.getDivisions(),
    staleTime: STALE_TIME.SHORT,
  });
}

/** 이 판의 부문 편성. 트리와 키를 나눈다 — 부문만 바뀌어도 트리를 다시 받을 이유가 없다. */
export function usePumsemEditionDivisions(edition: string | null) {
  return useQuery({
    queryKey: [...pumsemKeys.all, 'edition-divisions', edition] as const,
    queryFn: () => pumsemManageApi.getEditionDivisions(edition ?? undefined),
    staleTime: STALE_TIME.SHORT,
  });
}

export function usePumsemRevision(revisionId: string | undefined) {
  return useQuery({
    queryKey: [...pumsemKeys.all, 'revision', revisionId ?? ''] as const,
    queryFn: () => pumsemManageApi.getRevision(revisionId!),
    enabled: !!revisionId,
    staleTime: STALE_TIME.SHORT,
    retry: false,
  });
}

/**
 * 쓰기 뒤 무효화 범위.
 *
 * 트리와 검색을 **함께** 지운다 — 제목·코드가 트리에도 결과 카드에도 실려 있어서
 * 본문만 갱신하면 목록이 옛 제목을 유지한다(도움말과 같은 규약).
 *
 * 재정렬·부문 편성은 코드까지 바꾸므로 범위를 좁힐 수 없다. 관리 화면의 쓰기는
 * 초당 여러 번 일어나지 않으니 통째로 지우는 쪽이 정확하다.
 */
function useInvalidateContent() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: pumsemKeys.all });
  };
}

export function useCreatePumsemItem(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: PumsemItemInput) =>
      pumsemManageApi.createItem(edition, input),
    onSuccess: invalidate,
  });
}

export function useUpdatePumsemItem(revisionId: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: PumsemItemInput) =>
      pumsemManageApi.updateItem(revisionId, input),
    onSuccess: invalidate,
  });
}

export function useRemovePumsemItem() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (revisionId: string) => pumsemManageApi.removeItem(revisionId),
    onSuccess: invalidate,
  });
}

export function useCreatePumsemEdition() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: PumsemEditionInput) =>
      pumsemManageApi.createEdition(input),
    onSuccess: invalidate,
  });
}

/** 공개 / 숨김 토글. 발행 취소가 아니라 가시성만 바꾼다. */
export function useSetPumsemEditionVisibility() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: { slug: string; visible: boolean }) =>
      pumsemManageApi.setEditionVisibility(input.slug, input.visible),
    onSuccess: invalidate,
  });
}

export function useUpdatePumsemEdition() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: { slug: string; patch: PumsemEditionPatch }) =>
      pumsemManageApi.updateEdition(input.slug, input.patch),
    onSuccess: invalidate,
  });
}

export function useRemovePumsemEdition() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (slug: string) => pumsemManageApi.removeEdition(slug),
    onSuccess: invalidate,
  });
}

/** 일괄 임포트. 부문 하나를 통째로 갈아끼우므로 무효화 범위도 전체다. */
export function useImportPumsemDivision(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (payload: PumsemImportPayload) =>
      pumsemManageApi.importDivision(edition, payload),
    onSuccess: invalidate,
  });
}

export function useAttachPumsemDivision(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (division: string) =>
      pumsemManageApi.attachDivision(edition, division),
    onSuccess: invalidate,
  });
}

export function useDetachPumsemDivision(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (division: string) =>
      pumsemManageApi.detachDivision(edition, division),
    onSuccess: invalidate,
  });
}

export function useReorderPumsemDivisions(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (divisions: string[]) =>
      pumsemManageApi.reorderDivisions(edition, divisions),
    onSuccess: invalidate,
  });
}

export function useCreatePumsemDivision() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: PumsemDivisionInput) =>
      pumsemManageApi.createDivision(input),
    onSuccess: invalidate,
  });
}

export function useUpdatePumsemDivision() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: { slug: string; patch: PumsemDivisionPatch }) =>
      pumsemManageApi.updateDivision(input.slug, input.patch),
    onSuccess: invalidate,
  });
}

export function useRemovePumsemDivision() {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (slug: string) => pumsemManageApi.removeDivision(slug),
    onSuccess: invalidate,
  });
}

export function useCreatePumsemGroup(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: PumsemGroupInput) =>
      pumsemManageApi.createGroup(edition, input),
    onSuccess: invalidate,
  });
}

export function useUpdatePumsemGroup(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: PumsemGroupPatch) =>
      pumsemManageApi.updateGroup(edition, input),
    onSuccess: invalidate,
  });
}

export function useReorderPumsemGroups(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (input: PumsemGroupOrderInput) =>
      pumsemManageApi.reorderGroups(edition, input),
    onSuccess: invalidate,
  });
}

export function useRemovePumsemGroup(edition: string) {
  const invalidate = useInvalidateContent();
  return useMutation({
    mutationFn: (ref: { division: string; code: string }) =>
      pumsemManageApi.removeGroup(edition, ref.division, ref.code),
    onSuccess: invalidate,
  });
}
